import type { CSSProperties } from "react";
import { ADV, CAP, LETTER_PATHS, YA_BBOX, YA_PATH } from "../lib/logo-outlines";

/**
 * The Yabe ヤ mark and YABE wordmark, as real `<path>` vectors — no font
 * load required, renders identically everywhere (specs/brand, ADR-A32).
 * Color comes from CSS `color` (defaults to `currentColor`); callers that
 * need a fixed brand color set it explicitly, e.g. `style={{ color: "var(--amber)" }}`.
 * The header logo deliberately does *not* do this — it inherits the theme's
 * text color, which is the only way to stay off the "amber on paper" pairing
 * the brand guide rules out (specs/brand/logo-pack/README.md §Colour).
 */

const GW = YA_BBOX.x2 - YA_BBOX.x1; // 914
const GH = YA_BBOX.y2 - YA_BBOX.y1; // 905
const LETTERS = ["Y", "A", "B", "E"] as const;

function glyphTransform(x: number, y: number, h: number): string {
  const s = h / GH;
  return `translate(${x - YA_BBOX.x1 * s} ${y - YA_BBOX.y1 * s}) scale(${s})`;
}

/** The ヤ glyph alone, fit into a box of height `h` with its top-left at (x, y). */
function Glyph({ x, y, h }: { x: number; y: number; h: number }) {
  return (
    <g transform={glyphTransform(x, y, h)}>
      <path fill="currentColor" d={YA_PATH} />
    </g>
  );
}

function wordWidth(cap: number, track: number): number {
  const s = cap / CAP;
  return (4 * ADV + 3 * track * 1000) * s;
}

/** The YABE wordmark, baseline at (x, baseline), letter cap height `cap`, tracking `track` (em). */
function Word({
  x,
  baseline,
  cap,
  track,
}: {
  x: number;
  baseline: number;
  cap: number;
  track: number;
}) {
  const s = cap / CAP;
  const t = track * 1000;
  const step = ADV + t; // every letter advances the same amount (font units)
  return (
    <>
      {LETTERS.map((ch, i) => (
        <path
          key={ch}
          fill="currentColor"
          transform={`translate(${x + i * step * s} ${baseline}) scale(${s})`}
          d={LETTER_PATHS[ch]}
        />
      ))}
    </>
  );
}

type SvgProps = { className?: string | undefined; style?: CSSProperties | undefined };

/** Icon alone. `size` is the box height in px (brand minimum: 20). */
export function YabeMark({ size = 32, className, style }: SvgProps & { size?: number }) {
  const pad = size * 0.06;
  const h = size - pad * 2;
  const w = (h * GW) / GH;
  const W = w + pad * 2;
  return (
    <svg
      viewBox={`0 0 ${W} ${size}`}
      width={W}
      height={size}
      className={className}
      style={style}
      aria-hidden="true"
    >
      <Glyph x={pad} y={pad} h={h} />
    </svg>
  );
}

/**
 * Filled tile — confined squares (app icons, avatars). `size` is the tile
 * side in px (brand minimum: 24); `radius` is 0–0.5 as a fraction of size.
 * Unlike the other lockups this one is two-color by design (tile + glyph),
 * so it takes explicit colors instead of `currentColor`.
 */
export function YabeStamp({
  size = 32,
  radius = 0,
  tileColor = "var(--amber)",
  markColor = "var(--ink)",
  className,
  style,
}: SvgProps & {
  size?: number;
  radius?: number;
  tileColor?: string;
  markColor?: string;
}) {
  const h = size * 0.64;
  const w = (h * GW) / GH;
  const rx = radius ? size * radius : undefined;
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className={className}
      style={style}
      aria-hidden="true"
    >
      <rect width={size} height={size} {...(rx !== undefined ? { rx } : {})} fill={tileColor} />
      <g style={{ color: markColor }}>
        <Glyph x={(size - w) / 2} y={(size - h) / 2 + size * 0.015} h={h} />
      </g>
    </svg>
  );
}

/** Primary lockup — nav, headers, banners. `size` is the height in px (brand minimum: 24). */
function HorizLogo({ height, className, style }: SvgProps & { height: number }) {
  const pad = height * 0.08;
  const h = height - pad * 2;
  const w = (h * GW) / GH;
  const cap = h * 0.56;
  const gap = h * 0.36;
  const ww = wordWidth(cap, 0.18);
  const W = pad + w + gap + ww + pad;
  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      width={W}
      height={height}
      className={className}
      style={style}
      role="img"
      aria-label="YABE"
    >
      <Glyph x={pad} y={pad} h={h} />
      <Word x={pad + w + gap} baseline={pad + h / 2 + cap / 2 + h * 0.01} cap={cap} track={0.18} />
    </svg>
  );
}

/** Stacked lockup — splash, print, square crops. `size` is the width in px (brand minimum: 72). */
function StackedLogo({ width, className, style }: SvgProps & { width: number }) {
  const pad = width * 0.08;
  const cap0 = 340;
  const gap0 = 300;
  const wordW0 = wordWidth(cap0, 0.3);
  const contentW0 = Math.max(GW, wordW0);
  const s = (width - pad * 2) / contentW0;
  const h = GH * s;
  const cap = cap0 * s;
  const gap = gap0 * s;
  const H = pad + h + gap + cap + pad;
  const cx = width / 2;
  const ww = wordWidth(cap, 0.3);
  return (
    <svg
      viewBox={`0 0 ${width} ${H}`}
      width={width}
      height={H}
      className={className}
      style={style}
      role="img"
      aria-label="YABE"
    >
      <Glyph x={cx - (GW * s) / 2} y={pad} h={h} />
      <Word x={cx - ww / 2} baseline={pad + h + gap + cap} cap={cap} track={0.3} />
    </svg>
  );
}

/**
 * Full logo. Horizontal (default) is the primary lockup; `stacked` switches
 * to the vertical form. `size` is the height (horizontal) or width
 * (stacked) in px.
 */
export function YabeLogo({
  size = 28,
  stacked = false,
  className,
  style,
}: SvgProps & { size?: number; stacked?: boolean }) {
  return stacked ? (
    <StackedLogo width={size} className={className} style={style} />
  ) : (
    <HorizLogo height={size} className={className} style={style} />
  );
}
